import { CurrentClientIntegration } from "@/schema/current-client-integration"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import { apiContextFetch } from "@/hooks/api/base"
import { useApiContext } from "@/hooks/api/use-api-context"
import { useBookId } from "@/hooks/use-book-id"

interface AuthUriResponse {
  auth_uri: string
}

export const CURRENT_CLIENT_INTEGRATIONS_QUERY_KEY = ["current-client-integrations"]

export function useFetchCurrentClientIntegrations(enabled = true) {
  const getContext = useApiContext()
  const bookId = useBookId()

  return useQuery<CurrentClientIntegration[]>({
    queryKey: [...CURRENT_CLIENT_INTEGRATIONS_QUERY_KEY, bookId],
    queryFn: async () => {
      const context = await getContext()
      if (!context) throw new Error("No context found")

      const data = await apiContextFetch<CurrentClientIntegration[]>(context, "current_client_integrations")
      return data
    },
    enabled,
  })
}

export function useGetCurrentClientAuthUri() {
  const getContext = useApiContext()

  return useMutation<AuthUriResponse, Error, { returnUrl?: string; loginHint?: string } | void>({
    mutationKey: ["current-client-auth-uri"],
    mutationFn: async (params = {}) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")

      const searchParams = new URLSearchParams()
      if (params && "returnUrl" in params && params.returnUrl) {
        searchParams.append("return_url", params.returnUrl)
      }
      if (params && "loginHint" in params && params.loginHint) {
        searchParams.append("login_hint", params.loginHint)
      }

      const url = `current_client_integrations/get_auth_uri${searchParams.toString() ? `?${searchParams.toString()}` : ""}`
      return apiContextFetch<AuthUriResponse>(context, url)
    },
  })
}

export function useDisconnectCurrentClientIntegration() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()
  const bookId = useBookId()

  return useMutation<void, Error, { id: string }>({
    mutationKey: ["current-client-integration-disconnect"],
    mutationFn: async ({ id }) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")

      await apiContextFetch(context, `current_client_integrations/${id}`, {
        method: "PUT",
        body: JSON.stringify({ disconnect: true }),
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...CURRENT_CLIENT_INTEGRATIONS_QUERY_KEY, bookId] })
      queryClient.invalidateQueries({ queryKey: ["sms-messages"] })
      queryClient.invalidateQueries({ queryKey: ["call-logs"] })
    },
  })
}

export function useDeleteCurrentClientIntegration() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()
  const bookId = useBookId()

  return useMutation<void, Error, { id: string }>({
    mutationFn: async ({ id }) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")

      await apiContextFetch(context, `current_client_integrations/${id}`, {
        method: "DELETE",
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...CURRENT_CLIENT_INTEGRATIONS_QUERY_KEY, bookId] })
      queryClient.invalidateQueries({ queryKey: ["sms-messages"] })
      queryClient.invalidateQueries({ queryKey: ["call-logs"] })
    },
  })
}
